<template>
  <div class="container">
    <el-card class="card" shadow="never">
      <div slot="header" class="card-header">
        <h2>{{ $t("orderList") }}</h2>
      </div>
      <el-container>
        <!-- 导航菜单 -->
        <el-header>
          <div class="header-content">
            <Navbar
              :active-tab="activeTab"
              :tabs="orderTabs"
              @tab-change="handleTabChange"
            ></Navbar>
          </div>
        </el-header>

        <el-main>
          <!-- 筛选条件 -->
          <el-card class="filter-card">
            <el-form
              :inline="true"
              :model="filterForm"
              class="demo-form-inline"
            >
              <el-form-item :label="$t('searchOrder')">
                <el-input
                  v-model="filterForm.search"
                  :placeholder="$t('searchOrderPlaceholder')"
                ></el-input>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="handleFilter">
                  {{ $t("filter") }}
                </el-button>
                <el-button @click="resetFilter">
                  {{ $t("reset") }}
                </el-button>
              </el-form-item>
            </el-form>
          </el-card>

          <!-- 订单列表 -->
          <el-table :data="filteredOrders" stripe border v-loading="loading">
            <el-table-column
              prop="orderNo"
              :label="$t('orderNo')"
              width="180"
            ></el-table-column>
            <el-table-column
              prop="routeName"
              :label="$t('productName')"
              width="220"
            ></el-table-column>
            <el-table-column
              prop="departure"
              :label="$t('departure')"
              width="80"
            ></el-table-column>
            <el-table-column
              prop="destination"
              :label="$t('destination')"
              width="80"
            ></el-table-column>
            <el-table-column
              prop="travelDate"
              :label="$t('travelDate')"
              width="180"
            ></el-table-column>
            <el-table-column prop="price" :label="$t('price')" width="80">
              <template #default="scope">
                <span class="price">¥{{ scope.row.price }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="status" :label="$t('status')" width="130">
              <template #default="scope">
                <el-tag :type="getStatusType(scope.row.status)">{{
                  $i18n.locale === "en" ? scope.row.status_en : scope.row.status
                }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column
              prop="createTime"
              :label="$t('createTime')"
              width="120"
            >
            </el-table-column>
            <el-table-column :label="$t('operation')" width="200">
              <template #default="scope">
                <el-button
                  v-if="scope.row.status === '待支付'"
                  size="small"
                  type="primary"
                  @click="handleGoToPay(scope.row)"
                >
                  {{ $t("goToPay") }}
                </el-button>
                <el-button
                  v-if="scope.row.status === '待支付'"
                  size="small"
                  type="warning"
                  @click="handleCancelOrder(scope.row)"
                >
                  {{ $t("cancelOrder") }}
                </el-button>

                <el-button
                  v-if="
                    scope.row.status === '已完成' &&
                    scope.row.hasReviewed === false
                  "
                  size="small"
                  type="success"
                  @click="handleEvaluation(scope.row.orderNo)"
                >
                  {{ $t("shareReview") }}
                </el-button>
                <el-tag type="success" v-if="scope.row.hasReviewed === true">
                  {{ $t("reviewed") }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>

          <!-- 分页控件 -->
          <Pagination
            :total="total"
            :page-size="pageSize"
            :current-page="currentPage"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </el-main>
      </el-container>
    </el-card>
  </div>
</template>

<script>
import Navbar from "@/components/Navbar.vue";
import Pagination from "../../components/Pagination.vue";

export default {
  name: "OrderList",
  components: {
    Navbar,
    Pagination,
  },
  data() {
    return {
      filterForm: {
        search: "",
        destination: "",
      },
      activeTab: "all",
      total: 0,
      loading: false,
      orders: [],
      orderTabs: [
        { label: "allOrders", value: "all" },
        { label: "pendingPayment", value: "pending_payment" },
        { label: "pendingTravel", value: "pending_travel" },
        { label: "completed", value: "completed" },
      ],
      currentPage: 1,
      pageSize: 5,
    };
  },
  watch: {
    $route(to) {
      if (to.query.refresh === "1") {
        this.currentPage = 1;
        this.getOrderList();
        this.$router.replace({ query: {} });
      }
    },
  },
  computed: {
    filteredOrders() {
      if (this.activeTab === "all") {
        return this.orders;
      } else if (this.activeTab === "pending_payment") {
        return this.orders.filter((order) => order.status === "待支付");
      } else if (this.activeTab === "pending_travel") {
        return this.orders.filter((order) => order.status === "待出行");
      } else if (this.activeTab === "completed") {
        return this.orders.filter((order) => order.status === "已完成");
      }
      return [];
    },
  },
  created() {
    this.getOrderList();
  },
  methods: {
    async getOrderList() {
      this.loading = true;
      try {
        this.$get("/order/list", {
          activeTab: this.activeTab,
          search: this.filterForm.search,
          page: this.currentPage,
          pageSize: this.pageSize,
        }).then((res) => {
          if (res.data.code === 1) {
            this.orders = res.data.data.list;
            this.total = res.data.data.total;
          }
        });
      } catch (err) {
        this.$message.error(this.$t("loadOrderListFailed"));
      } finally {
        this.loading = false;
      }
    },
    handleFilter() {
      this.currentPage = 1;
      this.getOrderList();
    },
    resetFilter() {
      this.filterForm = {
        search: "",
        destination: "",
      };
      this.currentPage = 1;
      this.getOrderList();
    },
    getStatusType(status) {
      if (status === "待支付") return "warning";
      if (status === "待出行") return "info";
      if (status === "已完成") return "success";
      return "";
    },
    handleTabChange(tabValue) {
      this.activeTab = tabValue;
      this.getOrderList();
    },
    handleViewDetail(order) {
      this.$message.info(this.$t("viewOrderDetail") + order.orderNo);
    },
    handleCancelOrder(order) {
      this.$confirm(this.$t("confirmCancelOrder"), this.$t("tip"), {
        confirmButtonText: this.$t("confirm"),
        cancelButtonText: this.$t("cancel"),
        type: "warning",
      })
        .then(() => {
          this.$post("/order/cancel", { order_no: order.orderNo }).then(
            (res) => {
              if (res.data.code == 1) {
                this.$message.success(this.$t("orderCancelSuccess"));
                this.getOrderList();
              } else {
                this.$message.error(
                  res.data.msg || this.$t("orderCancelFailed"),
                );
              }
            },
          );
        })
        .catch(() => {
          this.$message.info(this.$t("cancelOrderClosed"));
        });
    },
    handleGoToPay(order) {
      const currentDate = new Date();
      const travelStartDate = new Date(order.start_date);

      if (isNaN(travelStartDate.getTime())) {
        this.$message.error(this.$t("travelDateFormatError"));
        return;
      }

      currentDate.setHours(0, 0, 0, 0);
      travelStartDate.setHours(0, 0, 0, 0);

      if (currentDate >= travelStartDate) {
        this.$message.warning(this.$t("orderExpiredCannotPay"));
        return;
      }

      const orderData = {
        orderNo: order.orderNo,
        routeName: order.routeName,
        departure: order.departure,
        destination: order.destination,
        startDate: order.start_date,
        endDate: order.end_date,
        adultCount: order.adultCount,
        roomCount: order.roomCount,
        totalAmount: order.price,
        travelPlanId: order.travel_plan_id,
        travelId: order.travel_id,
      };
      this.$router.push({
        path: "/travel/booking",
        query: {
          orderData: encodeURIComponent(JSON.stringify(orderData)),
          days: order.days,
          routeName: encodeURIComponent(order.routeName),
        },
      });
    },
    handleEvaluation(orderNo) {
      this.$router.push({
        path: "/about-me/my-listings/evaluation",
        query: { orderNo: orderNo },
      });
    },
    handleCurrentChange(page) {
      this.currentPage = page;
      this.getOrderList();
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.getOrderList();
    },
  },
};
</script>

<style scoped>
@import "@/assets/css/person-common.css";
.price {
  color: #ff5500;
  font-weight: bold;
}

@media (max-width: 768px) {
  ::v-deep .card > .el-card__body {
    padding: 0px !important;
  }
}
</style>
